import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertOctagon, AlertTriangle, ArrowRight, Sparkles, ChevronDown } from "lucide-react";

const PRIORITY = {
  critical: { color: "#F87171", bg: "rgba(248,113,113,0.14)", border: "rgba(248,113,113,0.55)", icon: AlertOctagon,   label: "CRITICAL" },
  high:     { color: "#FB923C", bg: "rgba(251,146,60,0.12)",  border: "rgba(251,146,60,0.55)",  icon: AlertTriangle, label: "HIGH" },
};

const SPECIALTY_ACCENT = {
  nephrology: "#4ADE80", cardiology: "#F87171",
  neurology: "#A78BFA",  oncology:   "#FBBF24",
};

/**
 * CrossSpecialtyAlerts — Provider Inbox review-needed panel.
 *
 * Reads cached cross-specialty AI insights (critical + high priority) for
 * every linked patient and surfaces them at the top of the inbox so doctors
 * see review-needed AI risks before drilling into any single chart.
 */
export default function CrossSpecialtyAlerts({ onOpenPatient, patients = [] }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/provider/cross-specialty-alerts");
        setAlerts(data.alerts || []);
      } catch {
        setAlerts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !alerts.length) return null;

  const handleOpen = (a) => {
    if (!onOpenPatient) return;
    const p = patients.find((x) => x.patient_id === a.patient_id || x.email === a.patient_email);
    if (p) onOpenPatient(p);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 pt-6" data-testid="cross-specialty-alerts">
      <div
        className="ros-card"
        style={{
          background: "rgba(248,113,113,0.05)",
          borderColor: "rgba(248,113,113,0.35)",
          borderLeftWidth: 2, borderLeftColor: "#F87171",
        }}
      >
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={14} className="text-[#A78BFA]" />
          <div className="ros-label" style={{ color: "#F87171" }}>
            AI cross-specialty review · {alerts.length} patient{alerts.length !== 1 ? "s" : ""} flagged
          </div>
          <span className="text-[10px] text-[#7A8480]">Med Nexus AI</span>
        </div>
        <div className="space-y-2">
          {alerts.map((a) => {
            const ins = a.insight || {};
            const p = PRIORITY[ins.priority] || PRIORITY.high;
            const Icon = p.icon;
            const isExp = expandedId === a.patient_id;
            return (
              <div
                key={a.id || a.toxicity_id || a.escalation_key || `${a.patient_id}::${a.source || "digest"}::${a.day_key || ""}::${a.created_at || ""}`}
                className="ros-card-raised"
                data-testid={`cross-alert-${a.patient_email || a.patient_id}`}
                style={{ background: p.bg, borderColor: p.border }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: `${p.color}22`, border: `1px solid ${p.color}55` }}
                    >
                      <Icon size={14} style={{ color: p.color }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                          style={{ color: p.color, background: `${p.color}22`, border: `1px solid ${p.color}55` }}
                        >
                          {p.label}
                        </span>
                        <button
                          onClick={() => handleOpen(a)}
                          data-testid={`cross-alert-open-${a.patient_email || a.patient_id}`}
                          className="text-sm font-semibold text-white hover:text-[#4ADE80] transition-colors truncate"
                        >
                          {a.patient_name}
                        </button>
                        {(ins.specialties_involved || []).slice(0, 4).map((s) => (
                          <span
                            key={s}
                            className="text-[9px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wider"
                            style={{
                              color: SPECIALTY_ACCENT[s] || "#A3ACA7",
                              background: `${SPECIALTY_ACCENT[s] || "#A3ACA7"}18`,
                              border: `1px solid ${SPECIALTY_ACCENT[s] || "#A3ACA7"}40`,
                            }}
                          >
                            {s}
                          </span>
                        ))}
                        {a.stale && (
                          <span className="text-[9px] text-[#FBBF24]">· stale</span>
                        )}
                      </div>
                      <div className="text-sm text-white mt-1.5 leading-snug">
                        {ins.headline}
                      </div>

                      {isExp && (ins.rationale || ins.recommendation) && (
                        <div className="mt-3 space-y-2" data-testid={`cross-alert-details-${a.patient_email || a.patient_id}`}>
                          {ins.rationale && (
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7] mb-1">
                                Rationale
                              </div>
                              <div className="text-xs text-[#E5E7EB] leading-relaxed">
                                {ins.rationale}
                              </div>
                            </div>
                          )}
                          {ins.recommendation && (
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7] mb-1 flex items-center gap-1">
                                <ArrowRight size={10} /> Recommended action
                              </div>
                              <div className="text-xs text-white leading-relaxed font-medium">
                                {ins.recommendation}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {(ins.rationale || ins.recommendation) && (
                        <button
                          onClick={() => setExpandedId(isExp ? null : a.patient_id)}
                          data-testid={`cross-alert-toggle-${a.patient_email || a.patient_id}`}
                          className="mt-2 text-[10px] flex items-center gap-1 opacity-80 hover:opacity-100 transition-opacity"
                          style={{ color: p.color }}
                        >
                          <ChevronDown size={10} className={isExp ? "rotate-180 transition-transform" : "transition-transform"} />
                          {isExp ? "Show less" : "Why & what to do"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
